/**
 * Utility functions for link detection and classification
 */

/**
 * Determines if a URL is external (not part of the current site)
 * External links include HTTP/HTTPS URLs, email links, and telephone links
 *
 * @param href - The URL to check
 * @returns true if the URL is external, false otherwise
 */
export const isExternalLink = (href?: string): boolean => {
  if (!href) return false;

  return href.startsWith('http') || href.startsWith('mailto:') || href.startsWith('tel:');
};

/**
 * Determines if a URL is a hash/anchor link within the same page
 * Recognizes both #anchor and /#anchor patterns
 *
 * @param href - The URL to check
 * @returns true if the URL is a hash link, false otherwise
 */
export const isHashLink = (href?: string): boolean => {
  if (!href) return false;

  return href.startsWith('#') || href.startsWith('/#');
};

/**
 * Determines if a URL is an internal link (part of the current site)
 * Internal links are those that are not external and not hash links
 *
 * @param href - The URL to check
 * @returns true if the URL is internal, false otherwise
 */
export const isInternalLink = (href?: string): boolean => {
  if (!href) return false;

  return !isExternalLink(href) && !isHashLink(href);
};

/**
 * Checks if a link is a same-page hash link
 * Handles both pure hash links (#section) and full path hash links (/page#section)
 * @param href - The href to check
 * @param currentPath - The current page path (optional, defaults to window.location.pathname)
 * @returns true if the link targets an anchor on the current page
 */
export const isSamePageLink = (href: string, currentPath?: string): boolean => {
  if (!href) return false;

  // Pure hash link (e.g., #section) - always same page
  if (href.startsWith('#')) {
    return true;
  }

  // Check if it's a cross-page hash link (e.g., /page#section)
  if (href.includes('#')) {
    const path = typeof window !== 'undefined' ? window.location.pathname : currentPath || '';
    const [linkPath] = href.split('#');

    // Normalize paths by removing trailing slashes
    const normalizedPath = path.replace(/\/$/, '') || '/';
    const normalizedLinkPath = linkPath.replace(/\/$/, '') || '/';

    // Same page if the paths match
    return normalizedPath === normalizedLinkPath;
  }

  return false;
};

/**
 * Handles smooth scrolling to an anchor element
 * @param targetId - The ID of the element to scroll to (without the #)
 * @param updateUrl - Whether to update the URL hash (default: true)
 */
export const smoothScrollToAnchor = (targetId: string, updateUrl: boolean = true): void => {
  const targetElement = document.getElementById(targetId);

  if (targetElement) {
    targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });

    if (updateUrl) {
      window.history.pushState(null, '', `#${targetId}`);
    }
  }
};

/**
 * Wraps an onClick handler with smooth scroll behavior for same-page anchor links
 * @param href - The link href to check if it's a same-page link
 * @param onClick - Optional existing onClick handler to call after smooth scrolling
 * @returns A new onClick handler with smooth scroll behavior
 */
export const withSmoothScroll = (
  href: string,
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void
) => {
  return (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (isSamePageLink(href)) {
      e.preventDefault();

      const targetId = href.includes('#') ? href.split('#')[1] : '';

      if (targetId) {
        smoothScrollToAnchor(targetId, true);
      }
    }

    // Call the original onClick handler if it exists
    if (onClick) {
      onClick(e);
    }
  };
};
